import { FC, useRef } from 'react';
import {
  FilledInput,
  FormControl,
  Input,
  InputLabel,
  MenuItem,
  OutlinedInput,
  SelectChangeEvent,
  SelectProps,
} from '@mui/material';

import { ItemText, SelectStyled } from './styles';
import { SelectFormControlObjectProps } from './types';

export function SelectFormControlObject<T>(props: SelectFormControlObjectProps<T>) {
  const {
    label,
    placeholder,
    variant,
    disabled,
    inputProps = {},
    selectProps = {} as SelectProps,
    selectedOption,
    options,
    getOptionValue,
    getOptionLabel = getOptionValue,
    onOptionSelect,
  } = props;

  const inputLabelRef = useRef(null);

  const input =
    variant === 'outlined' ? (
      <OutlinedInput label={label} name='input' id='input' {...inputProps} />
    ) : variant === 'filled' ? (
      <FilledInput name='input' id='input' {...inputProps} />
    ) : (
      <Input name='input' id='input' {...inputProps} />
    );

  const handleSelectOption = (event: SelectChangeEvent<unknown>) => {
    const selected = options.find((option: T) => getOptionValue(option) === event.target.value);

    if (selected !== undefined) {
      onOptionSelect(selected);
    }
  };

  return (
    <FormControl variant={variant} disabled={disabled || options.length === 0}>
      {label && (
        <InputLabel ref={inputLabelRef} htmlFor='currency'>
          {label}
        </InputLabel>
      )}

      <SelectStyled
        {...selectProps}
        value={selectedOption !== undefined ? getOptionValue(selectedOption) : ''}
        onChange={handleSelectOption}
        input={input}
        data-testid={`${label?.toLowerCase() || 'entity'}-select-label`}
        displayEmpty={true}
      >
        {placeholder && (
          <MenuItem value='' disabled={true}>
            {placeholder}
          </MenuItem>
        )}
        {options.map((opt: T, idx: number) => (
          <ItemText key={idx} value={getOptionValue(opt)}>
            {getOptionLabel(opt)}
          </ItemText>
        ))}
      </SelectStyled>
    </FormControl>
  );
}

function ofType<T>() {
  return SelectFormControlObject as FC<SelectFormControlObjectProps<T>>;
}

SelectFormControlObject.ofType = ofType;
